import type { AppClientId, OIDCGrantPublicId, ProductPlanPublicId, ProjectPublicId, UserPublicId, UserSessionPublicId } from '@flakiness/database';
import type { FlakinessReport as FK } from '@flakiness/flakiness-report';
import type { DataSnapshotId as DSID } from '../node/dataSnapshot.js';
import type { Git } from '../node/git.js';
import type { Stats } from './stats/stats.js';
import { TestOutcomes as T } from './stats/testOutcomes.js';
import { TimePeriod } from './timePeriod.js';

export namespace WireTypes {

  export type CommitRef =
    | { type: 'branch', name: string }
    | { type: 'pull-request', number: number, state: PullRequestState };

  export type DataSnapshotId = DSID;

  export type Commit = {
    commitId: FK.CommitId,
    timestamp: FK.UnixTimestampMS, // ms
    message: string,
    avatar_url?: string,
    author?: string,
    refs?: CommitRef[],
    // "Fake" commit means that it was synthesized.
    // Usually these are passed in those parts of the program
    // that require the Commit object, but clients know nothing
    // but the `commitId`.
    isFakeCommit?: boolean,
  };

  export type Ref = Git.Ref;

  export type PullRequestState = 'open' | 'closed' | 'merged';
  export type PullRequestStats = {
    url: string,
    headSha: string;
    headRef: string;
    baseRef: string;
    avatar_url?: string;
    author?: string;
    state: PullRequestState,
    number: number;
    title: string;
    createdTimestamp: FK.UnixTimestampMS;
    updatedTimestamp: FK.UnixTimestampMS;
    commitStats?: CommitStats;
    historyHead?: string,
  }

  export type ProjectVisibility = 'public'|'private';
  export type ProjectVisibilityType = 'regular'|'enterprise'|'open_source';
  export type ProjectRole = 'editor' | 'viewer';

  export type ProjectSnapshot = {
    updatedTimestamp: FK.UnixTimestampMS;
    reportBytes: number;
    attachmentBytes: number;
    runsCount: number;
    malformedRunsCount: number;
    testRunsCount: number;
    sparkline: DayMetrics[];
    defaultReport: {
      headCommitId?: string;
      testOutcomes: T.TestOutcomeCounts;
    };
  };

  // How the many results of a test over a group of commits collapse into one outcome: `eod`
  // takes its latest result, `worst` takes its worst one.
  export const OUTCOME_CLASSIFICATIONS = ['eod', 'worst'] as const;
  export type OutcomeClassification = typeof OUTCOME_CLASSIFICATIONS[number];

  // How the history of a test is bucketed: one bar per commit, or one per time period.
  export const HISTORY_TYPES = ['commit', ...TimePeriod.PERIODS] as const;
  export type HistoryType = typeof HISTORY_TYPES[number];

  export type Project = {
    projectPublicId: ProjectPublicId;
    projectSlug: string;
    runsCount: number;
    readWriteAccessToken?: string;
    sourceType: 'github_app'|'github_pat'|'gitlab_pat'|'local_checkout'|'json_repo';
    sourceLastFetchTime?: FK.UnixTimestampMS;
    sourceLastFetchHTTPCode?: number;
    sourceOwner: string,
    sourceRepo: string,
    sourceOrigin?: string,
    lastUploadTimestamp?: FK.UnixTimestampMS;
    visibility: ProjectVisibility;
    org: WireTypes.Organization;
    access: ProjectRole;
    reportTimeIsUploadTime: boolean;
    preferredDataRetentionDays?: number;
    preferredAttachmentsRetentionDays?: number;
    acceptableFlakinessRatio: number;
    timezone: string;
    // Default history bucketing for the report. Absent means 'commit'.
    defaultHistoryType?: HistoryType;
    githubChecksBranchCommits: boolean;
    githubChecksPullRequests: boolean;
    githubPullRequestComments: boolean;
    githubChecksHTTPCode?: number;
    githubPullRequestCommentsHTTPCode?: number;
    snapshot?: ProjectSnapshot;
  }

  export type OrgRole = 'admin' | 'member' | 'owner';

  // Upload activity of an organization, merged over its projects.
  export type OrganizationActivity = {
    lastUploadTimestamp?: FK.UnixTimestampMS;
    sparkline: DayMetrics[];
  };

  export type Organization = {
    orgSlug: string;
    // Organizations are public, and users accessing them might have no explicit role.
    access?: OrgRole;
    // Organization members get to see the limitations by billing.
    restrictedProjectAccess: boolean,
    restrictedCIUploads: boolean,
    /**
     * Allowed project visibilities for the org's current plan.
     */
    allowedVisibilities: ProjectVisibility[],
    // Only populated for admin/owner callers — gate via toWireOrg.
    // Nullable since orgs created before this column was added may not have one.
    billingEmail?: string,
    // True when the org has an active paid/trial subscription but no billing
    // email configured. Only populated for admin/owner callers (gated in
    // toWireOrg) since it reveals billing posture; undefined elsewhere.
    isMissingBillingEmail?: boolean,
    // True when a superuser granted this org the Premium Free implicit plan.
    // Only populated for admin/owner callers — gate via toWireOrg.
    premiumFreePlan?: boolean,
    // True when the org's current plan carries elite features (gating the org
    // audit log). Reflects billing posture, so only populated for admin/owner
    // callers — gate via toWireOrg; undefined elsewhere.
    eliteFeatures?: boolean,
    // Merged over every project of the org, private ones included, so only
    // the org-listing endpoints populate it: their callers are org members
    // or superusers and can see all of these projects anyway.
    activity?: OrganizationActivity,
  }

  export type BillingLimitations = 'restrict-project-access'|'restrict-project-access-except-ci-uploads';
  export type BillingStatus = {
    restrictedProjectAccess?: boolean,
    restrictedCIUploads?: boolean,
    subscription?: Subscription,
  }

  // These are only used when creating new plans.
  export type ProductPlanPrice = {
    priceId: string,
    billing: BillingPeriod,
    description: string,
  }

  export type DeviceAuthRequest = {
    clientId: AppClientId,
    deviceName: string,
    deviceCode: string,
  }

  export type BillingPeriod = 'month'|'day'|'week'|'year';
  export type ProductPlan = {
    /**
     * A unique ID of the plan.
     */
    id: ProductPlanPublicId,
    /**
     * Billing period of the plan
     */
    billing: BillingPeriod,
    /**
     * User-visible plan name
     */
    name: string,
    /**
     * Included license seats. Unlimited if undefined.
     */
    seats?: number,

    /**
     * Included storage in GB, where GB is 1000^3 bytes. Unlimited if undefined.
     */
    storage?: number,

    /**
     * Monthly price of the plan, in USD
     */
    price: number,
    /**
     * Included trial, if any. Undefined value means no trial was included.
     */
    trialDays?: number,
    /**
     * Maximum data retention in days.
     */
    maxDataRetentionDays: number,
    /**
     * If this plan is a "special offer" to a certain org,
     * then this is the slug of the org. Such plans can *only*
     * be installed on the matching orgs.
     */
    orgSlug?: string,
    /**
     * Project visibility type for this plan.
     */
    projectVisibilityType: ProjectVisibilityType,
    /**
     * Stripe price ID for seats.
     */
    priceIdSeats?: string,
    /**
     * Stripe price ID for the flat base fee, charged once per billing period.
     */
    priceIdBase?: string,
    /**
     * True for synthesized plans (the implicit Free plan) that have no
     * Stripe object behind them.
     */
    isImplicit?: boolean,
    /**
     * Plan-level switch for premium features (Slack alerts, dedicated support, etc.).
     * Wire-side is always a definite boolean; legacy DB rows with `null` are
     * resolved to `true` at the wire boundary.
     */
    premiumFeatures: boolean,
    /**
     * Plan-level switch for elite features (the org audit log). Only custom
     * (org-specific) plans may set this. Wire-side is always a definite boolean;
     * DB rows with `null` are resolved to `false` at the wire boundary.
     */
    eliteFeatures: boolean,
  }

  export type Subscription = {
    plan: ProductPlan,
    hasBillingIssues: boolean,
    trialEnd?: FK.UnixTimestampMS,
    periodStart: FK.UnixTimestampMS,
    periodEnd: FK.UnixTimestampMS,

    missingPaymentMethod: boolean,

    usedStorageBytes: number,
    usedSeats: number,

    /**
     * True when this subscription is synthesized for an org that has no
     * active Stripe subscription (the implicit Free plan).
     */
    isImplicit?: boolean,

    /**
     * When set, the subscription is scheduled to terminate at this moment
     * (e.g., after a downgrade-to-Free). The org keeps current plan benefits
     * until then, and afterwards falls through to the implicit Free plan.
     */
    cancelAt?: FK.UnixTimestampMS,
  }

  export type DayMetrics = {
    reportBytes: number,
    attachmentBytes: number,
    runsCount: number,
    testsCount: number,
    day: string,
  }

  export type User = {
    userId: UserPublicId;
    userName: string;
    userLogin: string;
    isSuperUser: boolean;
    avatarUrl?: string;
  }

  export type IdentityProvider = 'github'|'gitlab';

  // Self-only view. Includes private fields (email, trial state) that we must
  // NOT expose via list/search endpoints — those return WireTypes.User.
  export type CurrentUser = User & {
    email?: string,
    trialUsedAtSeconds?: number,
    linkedProviders: IdentityProvider[],
  }

  // Superuser-only view for the administration users page. Includes private
  // fields (email): never return it from non-administration endpoints.
  export type UserDetails = User & {
    email?: string,
    linkedProviders: IdentityProvider[],
    lastSeenTimestampSeconds?: number,
  }

  export type ProjectCollaborator = {
    user: User,
    userRole: ProjectRole,
  }

  export type OrgMember = {
    user: User,
    accessRole: OrgRole,
  }

  export type RunEnvironment = {
    envId: Stats.EnvId,
    category: string,
    name: string,
    configPath?: FK.GitFilePath;
    systemData: {
      osName: string,
      osVersion: string,
      osArch: string,
    },
    runtime?: {
      name: string,
      version: string,
    },
    userSuppliedData?: Record<string, string|boolean|number>,
  }

  // The commits of a report, and where tests' history starts; see `reportPreset` in the API.
  export type ReportPreset = {
    type: 'pr',
    number: number,
  } | {
    type: 'branch',
    // Omit for the default branch.
    name?: string,
    // Omit for the day of the branch head commit.
    day?: string,
  } | {
    type: 'commit',
    commitId: string,
    // Omit for the commit itself.
    historyHead?: string,
  };

  export type ListCommitOptions = {
    // commit filtering options
    head: string,
    headOffset?: number,
    maxCount?: number,
    sinceTimestamp?: FK.UnixTimestampMS,
    untilTimestamp?: FK.UnixTimestampMS,
  }
  
  export type Annotation = {
    annotationId: Stats.AnnotationId,
    type: string,
    description?: string,
  }

  export type TestError = {
    errorId: Stats.ErrorId,
    message?: string,
    value?: string,
  }

  // The average is computed by dividing one to another.
  export type AverageValue = {
    sum: number,
    count: number,
  }

  /**
   * the `idle` status is for a day that doesn't have any commits
   * the `untested` status is for a day that has commits that weren't tested.
   */
  export type SpanOutcome = 'regressed'|'expected'|'unexpected'|'skipped'|'flaked'|'untested'|'idle';

  export type Outcome = 'regressed'|'expected'|'unexpected'|'skipped'|'flaked';

  export type OutcomesCount = T.TestOutcomeCounts;

  export type Test = {
    testId: Stats.TestId,
    filePath?: FK.GitFilePath,
    titles: string[],
  };

  export type DailyOutcome = {
    day: string, // Temporal.PlainDate.toString()
    outcome: SpanOutcome,
    testStats: OutcomesCount,
  }

  export type DailyDuration = {
    day: string, // Temporal.PlainDate.toString()
    // The test's duration on the day's last tested commit, summed over timelines. Absent if the
    // test has no results that day.
    durationMs?: number,
  }

  export type PeriodSummary = {
    // The commits of the period, and how many of them the commit feed lists as tested: for the
    // request's timelines and, if the request has a test, for that test.
    commits: { total: number, tested: number },
    // The commit behind the period's outcome. With a test, it is the newest commit where the test
    // got the period's outcome; otherwise, the newest tested commit. Absent if nothing was tested.
    outcomeCommit?: {
      commitId: Stats.CommitId,
      // The day the feed lists the commit under, as `Temporal.PlainDate.toString()`.
      day: string,
    },
  }

  export type DayPeriodSummary = {
    // The days the period covers, `[since, until]`, as `Temporal.PlainDate.toString()`.
    since: string,
    until: string,
    // Not regressified: a failing test is always `unexpected`, and `regressed` is always 0.
    testStats: OutcomesCount,
    // The commits of the period, and how many of them reported tests in the timelines the
    // request asked for.
    commits: { total: number, tested: number },
    // The period this one was compared with, and how tests moved from it. Absent only for the
    // oldest period of the request, which has nothing before it.
    comparedTo?: {
      since: string,
      until: string,
      transitions: OutcomeTransition[],
    },
  }

  // Analytics compares outcomes that are not regressified, so failing tests are always `unexpected`.
  export const ANALYTICS_OUTCOMES = ['expected', 'unexpected', 'flaked', 'skipped'] as const;
  export type AnalyticsOutcome = typeof ANALYTICS_OUTCOMES[number];

  /**
   * Picks the tests whose outcome was `from` in the older period and `to` in the newer one. A
   * missing outcome means the test did not report in that period, as in `OutcomeTransition`.
   * A list of these is a union: a test matches if it matches any entry.
   */
  export type TransitionFilter = {
    from?: AnalyticsOutcome,
    to?: AnalyticsOutcome,
  };

  // The number of tests, counted once per environment, whose outcome was `from` in the older
  // period and `to` in the newer one. A missing outcome means the test did not report in that period.
  export type OutcomeTransition = {
    from?: AnalyticsOutcome,
    to?: AnalyticsOutcome,
    count: number,
  }

  export type SpanStats = {
    outcome: WireTypes.SpanOutcome,
    duration?: FK.DurationMS,
  }

  export type TestHistory = {
    commitId: Stats.CommitId,
    outcome: WireTypes.Outcome,
    duration?: FK.DurationMS,
    // What this bar covers: a single commit, or the period that starts at `sinceTimestamp`.
    historyType: HistoryType,
    sinceTimestamp: FK.UnixTimestampMS,
  }

  export type TestStats = {
    /**
     * TestIndex is the index of the test in the global test repository.
     * Unlike testId, test index might change in future, so it should never be
     * cached. For this reason, we also report it as part of testStats rather then
     * Test.
     */
    testIdx: Stats.TestIndex,
    /**
     * This is the environment inside the run.
     * Could be used to match to the FlakinessReport.TestRun
     */
    env: RunEnvironment,
    attemptIdx?: number,
    outcome: Outcome,
    flipRate?: number,
    failRate?: number,
    // Reverse-chronological
    history: TestHistory[],
    timeline: JSONEnvGroup,
    test: Test,
    commitId: FK.CommitId,
    runId: Stats.RunId,
    category: string,

    lineNumber: FK.Number1Based,
    tags?: string[],

    errors?: TestError[],

    hasVideo?: boolean,
    hasImage?: boolean,
    hasTrace?: boolean,

    durationMs: FK.DurationMS,
    durationChangeMs: FK.DurationMS,

    annotations?: Annotation[],
  }

  export type UserSession = {
    sessionPublicId: UserSessionPublicId,
    name?: string,
    clientId: AppClientId,
    lastAccessTimestampSeconds: number,
    lastUserAgent?: string,
    lastLocation?: {
      city?: string,
      region?: string,
      countryCode?: string,
    },
  }

  export type TimelineStats = {
    timeline: JSONEnvGroup,
    durationMs: number,
    testStats: OutcomesCount,
  }

  export type ErrorStats = {
    error: WireTypes.TestError,
    impactedTests: number,
    impactedTimelines: number,
  }

  export type AnnotationStats = {
    annotation: WireTypes.Annotation,
    annotatedTests: number,
    annotatedTimelines: number,
  }

  export type TagStats = {
    tag: string,
    impactedTests: number,
    impactedTimelines: number,
  }

  export type OIDCProvider = 'github' | 'gitlab';

  // A repository, other than the linked one, whose CI workflows may
  // authenticate to the project with an OIDC token.
  export type OIDCGrant = {
    grantId: OIDCGrantPublicId,
    provider: OIDCProvider,
    // Every namespace level above the repository, as of the last use.
    ownerName: string,
    repoName: string,
    createdTimestampSeconds: number,
    lastUsedTimestampSeconds?: number,
  }

  export type GithubRepository = {
    ownerType: string,
    avatarUrl: string,
    owner: string,
    name: string,
    private: boolean,
  }

  export type GitlabProject = {
    // Every namespace level above the project: `group/subgroup`.
    owner: string,
    name: string,
    avatarUrl?: string,
    isPrivate: boolean,
  }

  export type CommitFeedElement = {
    type: 'commit',
    day: string, // ISO plain date YYYY-MM-DD in requested timeZoneId
    commit: WireTypes.Commit,
    stats?: WireTypes.CommitStats,
  } | {
    type: 'collapsed_group',
    day: string, // ISO plain date YYYY-MM-DD in requested timeZoneId
    // These commits are always untested commits; to expand the group, call
    // history.expandCommitGroup with this head, offset and count. That endpoint
    // labels the commits with the same walk days this group was built from.
    head: string,
    count: number,
    offset: number,
  };
  export type CommitFeed = {
    feed: CommitFeedElement[],
    continuation?: Stats.CommitId
  }

  export type CommitStats = {
    commit: Commit,
    durationMs?: FK.DurationMS,
    testStats: OutcomesCount,
    runsCount: number,
    // The run that best characterizes the commit's outcome: worst outcome first, latest run on ties.
    // Set only for stats built from full commit reports, e.g. the per-test commit feed.
    representativeRunId?: Stats.RunId,
  }

  export type MalformedRun = {
    runId: Stats.RunId,
    uploadTimestamp: FK.UnixTimestampMS,
    error: string,
  }

  export type WellformedRun = {
    runId: Stats.RunId,
    url?: string,
    title?: string,
    startTimestamp: FK.UnixTimestampMS,
    uploadTimestamp: FK.UnixTimestampMS,
    duration: FK.DurationMS,
    commitId: FK.CommitId,
    // If commit is missing, then this run is from an "orphaned commit": a commit that
    // we failed to load. Maybe it's some local commit or something.
    commit?: WireTypes.Commit,
    testStatsOutcomes: OutcomesCount,
    environments: WireTypes.RunEnvironment[],
    // Sizes might be missing for runs indexed before size tracking was introduced.
    reportBytes?: number,
    attachmentBytes?: number,
  }

  export type ServerInfo = {
    isSingleUserMode: boolean,
    enabledBilling: boolean,
    enabledGithub: boolean,
    enabledGitlab: boolean,
    gitlabOrigin: string,
    enabledFSGitManager: boolean,
    fsGitManagerRoot?: string,
    githubAppPublicURL?: string,
    hostname: string,
    uptimeMs: number,
    memory: {
      usedBytes: number,
      totalBytes: number,
      timestampMs: number,
    },
    snapshots: {
      total: number,
      projects: number,
    },
    isDevMode: boolean,
    deploymentLicense: {
      validUntilMs: number,
    }
  }

  export type Job = {
    queueName: string;
    workerName?: string,
    heartbeatTimestampSeconds?: number,
    id: number;
    jobId: string;
    category?: string;
    concurrencyId?: string;
    data?: any;
    // -- execution args
    executeAfterTimestampSeconds?: number, // delayed job
    executionError?: string;
    executionResult?: string;
    executionDurationSeconds?: number,
    executionTimestampSeconds?: number,
    submittedTimestampSeconds: number;
    retry: number;
    priority: number;
  }

  export type SortDirection = 'asc'|'desc';

  export const SORT_AXES = {
    tests: [
      'name',
      'timeline_name',
      'duration',
      'outcome',
      'duration_trend',
      'flip_rate',
      'fail_rate',
    ],
    timelines: [
      'name',
      'outcome',
      'total_time',
      'unexpected',
      'expected',
      'skipped',
      'flaked',
      'regressed',
    ],
    errors: [
      'name',
      'timelines',
      'tests',
    ],
    annotations: [
      'name',
      'timelines',
      'tests',
    ],
    tags: [
      'name',
      'timelines',
      'tests',
    ],
    commits: [
      'chrono',
      'outcome',
      'unexpected',
      'expected',
      'skipped',
      'flaked',
      'regressed',
    ],
    runs: [
      'id',
      'outcome',
      'chrono',
      'unexpected',
      'expected',
      'skipped',
      'flaked',
      'factual_duration',
      'regressed',
    ],
    pullRequests: [
      'created',
    ],
    users: [
      'name',
      'login',
      'email',
      'last_seen',
    ],
  } as const;

  export type TestStatsSortAxis = typeof SORT_AXES.tests[number];
  export type TimelineStatsSortAxis = typeof SORT_AXES.timelines[number];
  export type ErrorStatsSortAxis = typeof SORT_AXES.errors[number];
  export type AnnotationStatsSortAxis = typeof SORT_AXES.annotations[number];
  export type TagStatsSortAxis = typeof SORT_AXES.tags[number];
  export type CommitStatsSortAxis = typeof SORT_AXES.commits[number];
  export type RunStatsSortAxis = typeof SORT_AXES.runs[number];
  export type PullRequestSortAxis = typeof SORT_AXES.pullRequests[number];
  export type UserSortAxis = typeof SORT_AXES.users[number];
  export type PagedResponse<T> = {
    elements: T[],
    pageNumber: number,
    pageSize: number,
    totalPages: number,
    totalElements: number,
  }

  export type AuditLogEntry = {
    action: string,
    createdTimestampSeconds: number,
    orgId?: number,
    projectId?: number,
    actorId?: number,
    actorName?: string,
    actorLogin?: string,
    actorIsSuperuser?: boolean,
    orgSlug?: string,
    projectSlug?: string,
    ip?: string,
    userAgent?: string,
    details: Record<string, unknown>,
  }

  export type Day = {
    sinceTimestamp: FK.UnixTimestampMS,
    untilTimestamp: FK.UnixTimestampMS,
  }

  export type ReportOptions = {
    orgSlug: string,
    projectSlug: string,
    snapshotId: DataSnapshotId,
    fql: string,
    timelineSplit: JSONEnvGroup,
    commitOptions: ListCommitOptions,
    timeZoneId: string,
    // The client picks how history is bucketed and may pin a custom head.
    // The server fills in the `timezone` for period history from `timeZoneId`.
    historyConfig?: { type: HistoryType, customHistoryHead?: string },
    acceptableFlakinessRate: number,
  }

  export type JSONEnvGroup = {
    type: 'open'|'closed',
    eq?: Record<string, string|string[]>,
    drop?: string[],
  }

  export type PageOptions = {
    number: number,
    size: number,
  }

  export type SortOptions<K> = {
    axis: K,
    direction: WireTypes.SortDirection,
  }

  export const EMPTY_OUTCOMES: WireTypes.OutcomesCount = T.newOutcomeCounts();
}

export function visibilityTypeToAllowedVisibilities(type?: WireTypes.ProjectVisibilityType): WireTypes.ProjectVisibility[] {
  // Default to enterprise-level project visibility if not given.
  type ??= 'enterprise';
  if (type === 'regular')
    return ['public', 'private'];
  if (type === 'open_source')
    return ['public'];
  return ['public', 'private'];
}

export function wireOutcomesToOutcome(outcomes: WireTypes.OutcomesCount, acceptableFlakinessRatio: number): WireTypes.Outcome|undefined {
  if (outcomes.regressed)
    return 'regressed';
  if (outcomes.unexpected)
    return 'unexpected';
  const total = outcomes.regressed + outcomes.unexpected + outcomes.flaked + outcomes.expected + outcomes.skipped;
  const flakinessRatio = outcomes.flaked / total;
  if (flakinessRatio > acceptableFlakinessRatio)
    return 'flaked';
  if (outcomes.expected || outcomes.flaked)
    return 'expected';
  if (outcomes.skipped)
    return 'skipped';
  return undefined;
}

export function wireOutcomesToSpanOutcome(outcomes: WireTypes.OutcomesCount, acceptableFlakinessRatio: number, hasCommits: boolean): WireTypes.SpanOutcome {
  return wireOutcomesToOutcome(outcomes, acceptableFlakinessRatio) ?? (hasCommits ? 'untested' : 'idle');
}

export function createFakeCommit(commitId: Stats.CommitId): WireTypes.Commit {
  return {
    isFakeCommit: true,
    commitId,
    timestamp: Date.now() as any,
    message: '',
  };
}
